<template>
  <CstuModal
    id="add-style-modal"
    title="Add Style"
    content-class="w-[650px]"
    dialog-class="!max-w-[650px]"
    body-class="no-scrollbar"
    z-index="1020"
    size="lg"
    radius="xl"
    hide-footer
    centered
    no-close-on-backdrop
    @shown="handleShown"
  >
    <div v-if="showLoader" class="h-[560px]">
      <AiGenerationLoader
        text="AI is generating your brand style!"
        :loading-completed="loadingCompleted"
        :loading-steps="loadingSteps"
        :is-content-library="true"
      />
    </div>

    <template v-if="styleAnalyzed">
      <CustomTab mode="style" />
    </template>


    <template v-else-if="!showLoader && !styleAnalyzed">
    <!-- Tabs -->
    <div class="flex items-center gap-x-4">
      <!-- Tab Headers -->
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="[
          selectedTab === tab.id ? 'cstu-text-blue-500 cstu-bg-blue-50' : '',
          'select-none text-sm rounded-full py-2 hover:cstu-bg-blue-50 px-3 flex items-center hover:cstu-text-blue-500 cursor-pointer gap-x-2',
        ]"
        @click="selectedTab = tab.id"
      >
        <i :class="[tab.icon]" aria-hidden="true"></i>
        <span class="text-base">{{ tab.name }}</span>
      </div>
    </div>

    <!-- Tab Content -->
      <div  class="mt-4" :class="{ 'h-[300px] 2xl:h-[400px] overflow-auto no-scrollbar transition-all': selectedTab === 'social' }"> <!-- Added a key to the div wrapping the components -->
        <WebsiteTab v-if="selectedTab === 'website'" mode="style" />
        <SocialTab v-else-if="selectedTab === 'social'" mode="style" />
        <CustomTab v-else-if="selectedTab === 'custom'" mode="style" />
      </div>
  </template>

    <!-- Footer -->
    <div
      v-if="!showFooter && !showLoader"
      class="flex items-center justify-between px-2 pt-4"
    >
    <a
      href="#"
      class="text-sm text-blue-500 beacon"
      data-beacon-article-modal="680f6c1a04f4b009e468e638"
    >About AI Library</a>

      <Button v-if="selectedTab === 'custom' || styleAnalyzed" size="sm" @click="handleAddCustomStyle">Save</Button>

      <Button v-else size="sm" :loading="isLoading" @click="handleAnalyze">
        Generate Style
      </Button>
    </div>
</CstuModal>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { Button } from '@contentstudio/ui'
import CustomTab from '@modules/publisher/ai-content-library/components/form/tabs/CustomTab.vue'
import { useSetup } from '@modules/publisher/ai-content-library/composables/useSetup'
import { useBrandInfoSetting } from '@modules/publisher/ai-content-library/composables/useBrandInfoSetting'
import WebsiteTab from '@/src/modules/publisher/ai-content-library/components/form/tabs/WebsiteTab.vue'
import SocialTab from '@/src/modules/publisher/ai-content-library/components/form/tabs/SocialTab.vue'
import { useStore } from '@/src/store/base'
import AiGenerationLoader from '@/src/components/common/AiGenerationLoader.vue'

const { isLoading, setupInfo, resetSetupInfo,  validateWebsiteUrl: validateWebsiteUrlFn } = useSetup()
const { addStyleCustom, handleAddStyle, showLoader ,styleAnalyzed} = useBrandInfoSetting()
const { dispatch, getters } = useStore()

const root = inject('root')
const { $cstuModal } = root

const hasSocialSetup = computed(() => {
  // Check if any social platform has selected accounts
  return getters.getSocialAccountsList?.length > 0
})

const selectedTab = ref('website')
const tabs = [
  {
    id: 'website',
    name: 'Website',
    icon: 'far fa-link',
  },
  {
    id: 'social',
    name: 'Social Accounts',
    icon: 'icon-social-accounts-cs',
  },
  {
    id: 'custom',
    name: 'Custom Style',
    icon: 'fa fa-paint-brush',
  },
]
const loadingSteps = ref([
  { message: 'Initializing', status: false },
  { message: 'Extracting data', status: false },
  { message: `Analyzing brand info`, status: false },
  { message: `Generating!`, status: false },
])
const loadingCompleted = ref(false)

const showFooter = computed(() => {
  return selectedTab.value === 'social' && !hasSocialSetup.value
})


const validateWebsiteUrl = () => {
  const { isValid, message, url } = validateWebsiteUrlFn(setupInfo.website.url)

  if (!isValid) {
    dispatch('toastNotification', {
      message,
      type: 'error',
    })
    return false
  }

  // Update the URL with the processed value
  setupInfo.website.url = url
  return true
}

const handleAnalyze = async () => {
  if (showFooter.value) {
    selectedTab.value = 'social'
    return
  }

  const hasSocialAccountSelected = Object.values(setupInfo.social).some(accounts => accounts.length > 0)

  // Validate if user has added any information
  if (!setupInfo.website.url && !hasSocialAccountSelected) {
    dispatch('toastNotification', {
      message: 'Please add at least one source so your Brand Style can be generated.',
      type: 'error',
    })
    return
  }

  // Validate website URL
  if (setupInfo.website.url && !validateWebsiteUrl()) {
    return
  }

  await handleAddStyle()
}

const handleAddCustomStyle = async () => {
  const result = await addStyleCustom()
  if (result) {
    $cstuModal.hide('add-style-modal')
  }
}


const handleShown = () => {
  console.log('handleShown')
  resetSetupInfo()

  // reset
  selectedTab.value = 'website'
  showLoader.value = false
  styleAnalyzed.value = false

}
</script>
